<div class="e360" [class.e360--embedded]="embedded" *ngIf="!loading && data">

  <!-- ═══════ Header ═══════ -->
  <header class="e360-header">
    <!-- Host-specific leading control (e.g. Team Composition's People drawer) -->
    <ng-content select="[e360-leading]"></ng-content>
    <div class="e360-avatar">{{ initials() }}</div>
    <div class="e360-header__identity">
      <div class="e360-header__name-row">
        <h1 class="e360-header__name">{{ data.identity.name }}</h1>
        <span class="e360-badge-emp">{{ data.identity.poiType }}</span>
        <span class="e360-badge-level" *ngIf="data.identity.careerLevel">
          {{ data.identity.careerLevel }} · {{ data.identity.jobTitle }}
        </span>
        <span class="e360-badge-level e360-badge-level--auto" *ngIf="!data.identity.careerLevel">
          {{ data.identity.jobTitle }}
        </span>
      </div>
      <div class="e360-chain">
        <span class="e360-chain__crumb">💼 CBRE · {{ data.identity.city }}</span>
        <span class="e360-chain__dot">·</span>
        <span class="e360-chain__crumb" *ngIf="data.identity.tenureYears != null">
          📅 {{ data.identity.tenureYears }} yrs · joined {{ data.identity.joined | date:'yyyy-MM-dd' }}
        </span>
        <span class="e360-chain__dot">·</span>
        <span class="e360-chain__crumb">👤 Mgr: <strong>{{ data.identity.managerName || '—' }}</strong></span>
        <span class="e360-chain__dot" *ngIf="data.identity.l3Name">→</span>
        <span class="e360-chain__crumb" *ngIf="data.identity.l3Name"><strong>{{ data.identity.l3Name }}</strong> (L3)</span>
      </div>
    </div>
    <div class="e360-header__actions">
      <!-- Host-specific actions (e.g. Team Composition's Add/View Feedback) -->
      <ng-content select="[e360-actions]"></ng-content>
    </div>
  </header>

  <div class="e360-page">

    <!-- ═══════ KPI row 1 — talent snapshot ═══════ -->
    <div class="e360-kpi-row">

      <a class="e360-kpi-tile" (click)="openProfile('profile')" title="Open product fitment">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--green">🎯</span>Product fit<span class="e360-info" [attr.data-tip]="tips.productFit" (click)="$event.stopPropagation()">i</span></span>
        <span class="e360-kpi-tile__value" *ngIf="data.productFit.avgPct != null">
          {{ data.productFit.avgPct }}<span class="e360-kpi-tile__unit">%</span>
        </span>
        <span class="e360-kpi-tile__value e360-kpi-tile__value--sm" *ngIf="data.productFit.avgPct == null">—</span>
        <span class="e360-kpi-tile__caption">
          {{ data.productFit.productCount > 0
             ? 'avg fitment · ' + data.productFit.productCount + ' assigned products'
             : 'no products assigned' }}
        </span>
      </a>

      <a class="e360-kpi-tile" (click)="openProfile('calibration')" title="Open calibration">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--blue">🧭</span>6-Box<span class="e360-info" [attr.data-tip]="tips.sixBox" (click)="$event.stopPropagation()">i</span></span>
        <div class="e360-kpi-tile__flex" *ngIf="data.sixBox.boxNumber != null; else noBox">
          <div class="e360-sixbox-mini">
            <span class="e360-sixbox-mini__cell" *ngFor="let current of sixBoxCells()"
                  [class.e360-sixbox-mini__cell--current]="current"></span>
          </div>
          <div class="e360-kpi-tile__stack">
            <div class="e360-kpi-tile__value e360-kpi-tile__value--box">{{ sixBoxName() }}</div>
            <span class="e360-kpi-tile__caption" *ngIf="data.sixBox.calibratedAt">
              Calibrated {{ data.sixBox.calibratedAt | date:'d MMM y' }}
            </span>
            <span class="e360-kpi-tile__caption" *ngIf="!data.sixBox.calibratedAt">{{ humanize(data.sixBox.status) }}</span>
          </div>
        </div>
        <ng-template #noBox>
          <span class="e360-kpi-tile__value e360-kpi-tile__value--sm">Not calibrated</span>
          <span class="e360-kpi-tile__caption">{{ humanize(data.sixBox.status) }}</span>
        </ng-template>
      </a>

      <a class="e360-kpi-tile" (click)="openProfile('tmap')" title="Open T-Map">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--mint">⚡</span>T-Score<span class="e360-info" [attr.data-tip]="tips.tScore" (click)="$event.stopPropagation()">i</span></span>
        <div class="e360-kpi-tile__flex" *ngIf="data.tScore; else noScore">
          <div class="e360-kpi-donut">
            <svg viewBox="0 0 68 68" aria-hidden="true">
              <circle cx="34" cy="34" r="28" fill="none" stroke="var(--e360-neutral-100)" stroke-width="8"/>
              <circle cx="34" cy="34" r="28" fill="none" stroke="var(--e360-projected)" stroke-width="8"
                      [attr.stroke-dasharray]="donutProjectedDash" stroke-linecap="round" transform="rotate(-90 34 34)"/>
              <circle cx="34" cy="34" r="28" fill="none" stroke="var(--e360-brand)" stroke-width="8"
                      [attr.stroke-dasharray]="donutCurrentDash" stroke-linecap="round" transform="rotate(-90 34 34)"/>
            </svg>
            <div class="e360-kpi-donut__lbl">
              <span class="e360-kpi-donut__n">{{ data.tScore.current }}</span>
              <span class="e360-kpi-donut__cap">now</span>
            </div>
          </div>
          <div class="e360-kpi-tile__stack">
            <div class="e360-kpi-tile__value e360-kpi-tile__value--sm" *ngIf="data.tScore.projected != null">
              → {{ data.tScore.projected }}
              <span class="e360-kpi-tile__delta">+{{ data.tScore.projected - data.tScore.current }}</span>
            </div>
            <span class="e360-kpi-tile__caption">
              {{ data.tScore.projected != null ? 'if targets met · ' : '' }}benchmark {{ data.tScore.benchmark }}
            </span>
          </div>
        </div>
        <ng-template #noScore>
          <span class="e360-kpi-tile__value e360-kpi-tile__value--sm">Auto</span>
          <span class="e360-kpi-tile__caption">no T-Map / level mapped yet</span>
        </ng-template>
      </a>

      <a class="e360-kpi-tile" (click)="openProfile('profile')" title="Open profile (attrition)">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--rose">🚪</span>Attrition risk <span class="e360-mgr-tag">MGR</span><span class="e360-info" [attr.data-tip]="tips.attrition" (click)="$event.stopPropagation()">i</span></span>
        <div class="e360-kpi-tile__pillwrap" *ngIf="data.attrition?.risk; else noRisk">
          <span class="e360-risk-pill"
                [class.e360-risk-pill--low]="data.attrition!.risk!.toLowerCase() === 'low'"
                [class.e360-risk-pill--med]="data.attrition!.risk!.toLowerCase() === 'medium'"
                [class.e360-risk-pill--high]="data.attrition!.risk!.toLowerCase() === 'high'">
            <span class="e360-risk-pill__dot"></span>{{ data.attrition!.risk | uppercase }}
          </span>
        </div>
        <ng-template #noRisk>
          <span class="e360-kpi-tile__value e360-kpi-tile__value--sm">—</span>
        </ng-template>
        <span class="e360-kpi-tile__caption">
          Fungible skills: {{ data.attrition?.fungibleSkills == null ? '—' : (data.attrition!.fungibleSkills ? 'yes' : 'no') }}
        </span>
      </a>

      <a class="e360-kpi-tile" (click)="openProfile('profile')" title="Open profile (talent & succession)">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">👑</span>Talent &amp; Succession <span class="e360-mgr-tag">MGR</span><span class="e360-info" [attr.data-tip]="tips.succession" (click)="$event.stopPropagation()">i</span></span>
        <ng-container *ngIf="data.succession as su; else noSuccession">
          <div class="e360-tags e360-tags--tile" *ngIf="su.topTalent || su.criticalTalent">
            <span class="e360-tag e360-tag--gold" *ngIf="su.topTalent">⭐ Top Talent</span>
            <span class="e360-tag" *ngIf="su.criticalTalent">Critical Talent</span>
          </div>
          <span class="e360-kpi-tile__value e360-kpi-tile__value--sm" *ngIf="!su.topTalent && !su.criticalTalent">No talent tags</span>
          <span class="e360-kpi-tile__caption">
            Successor: {{ su.successorRequired !== true ? 'not required'
                          : su.successorIdentified === true
                            ? ('identified' + (su.successorReadiness ? ' · ' + humanize(su.successorReadiness) : ''))
                            : 'required · not identified' }}
          </span>
        </ng-container>
        <ng-template #noSuccession>
          <span class="e360-kpi-tile__value e360-kpi-tile__value--sm">—</span>
          <span class="e360-kpi-tile__caption">no talent planning record yet</span>
        </ng-template>
      </a>
    </div>

    <!-- ═══════ KPI row 2 — AI usage ═══════ -->
    <div class="e360-kpi-row e360-kpi-row--second">

      <!-- Agents used — live once the agents export is loaded -->
      <div class="e360-kpi-tile e360-kpi-tile--static" *ngIf="data.agents as ag; else agentsPending">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">🕹️</span>Agents used <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.agentsUsed">i</span></span>
        <span class="e360-kpi-tile__value">{{ ag.agentCount }}<span class="e360-kpi-tile__unit">/4</span>
          <span class="e360-agile-tag" *ngIf="ag.agile">Agile</span>
        </span>
        <div class="e360-agent-segs">
          <span class="e360-agent-seg" *ngFor="let a of agentNames"
                [class.e360-agent-seg--on]="ag.agents.includes(a)" [title]="a"></span>
        </div>
        <span class="e360-kpi-tile__caption">
          {{ ag.agents.length ? ag.agents.join(' · ') : 'no SDLC agents yet' }}
        </span>
      </div>
      <ng-template #agentsPending>
        <div class="e360-kpi-tile e360-kpi-tile--pending">
          <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">🕹️</span>Agents used <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.agentsUsed">i</span></span>
          <span class="e360-pending-mark">⏳</span>
          <span class="e360-kpi-tile__caption">no agent-usage data recorded</span>
        </div>
      </ng-template>

      <!-- AIC utilized — live once the AIC export is loaded -->
      <div class="e360-kpi-tile e360-kpi-tile--static" *ngIf="data.aiUsage as ai; else aicUtilPending">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">🔋</span>AIC utilized <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.aicUtilized">i</span></span>
        <span class="e360-kpi-tile__value"><span class="e360-kpi-tile__unit">$</span>{{ ai.actualSpend != null ? compact(ai.actualSpend) : '—' }}
          <span class="e360-overuse-pill" *ngIf="(ai.excessSpend || 0) > 0">${{ compact(ai.excessSpend) }} over caps</span>
        </span>
        <span class="e360-kpi-tile__caption">
          {{ compact(ai.utilizedAic) }} AIC · cumulative · cap ${{ ai.monthlyCapUsd }}/mo
        </span>
      </div>
      <ng-template #aicUtilPending>
        <div class="e360-kpi-tile e360-kpi-tile--pending">
          <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">🔋</span>AIC utilized <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.aicUtilized">i</span></span>
          <span class="e360-pending-mark">⏳</span>
          <span class="e360-kpi-tile__caption">no AI Console usage recorded</span>
        </div>
      </ng-template>

      <!-- Top models — live once the AIC export is loaded -->
      <div class="e360-kpi-tile e360-kpi-tile--static" *ngIf="data.aiUsage as ai; else topModelsPending">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">🤖</span>Top models · AIC <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.topModels">i</span></span>
        <div class="e360-model-list">
          <div class="e360-model" *ngFor="let m of ai.topModels | slice:0:3">
            <div class="e360-model-row">
              <span class="e360-model-name" [title]="m.model">{{ m.model }}</span>
              <span class="e360-model-share">{{ m.sharePct != null ? m.sharePct + '%' : compact(m.utilizedAic) }}</span>
            </div>
            <div class="e360-model-bar"><span [style.width.%]="m.sharePct || 0"></span></div>
          </div>
        </div>
        <span class="e360-kpi-tile__caption">{{ ai.modelCount }} model{{ ai.modelCount === 1 ? '' : 's' }} used</span>
      </div>
      <ng-template #topModelsPending>
        <div class="e360-kpi-tile e360-kpi-tile--pending">
          <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">🤖</span>Top models · AIC <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.topModels">i</span></span>
          <span class="e360-pending-mark">⏳</span>
          <span class="e360-kpi-tile__caption">no AI Console usage recorded</span>
        </div>
      </ng-template>

      <!-- Copilot activity — live once the Copilot export is loaded -->
      <div class="e360-kpi-tile e360-kpi-tile--static" *ngIf="data.copilot as cp; else copilotPending">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">📅</span>Copilot · days active <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.copilotDays">i</span></span>
        <span class="e360-kpi-tile__value">{{ cp.daysLast30 }}<span class="e360-kpi-tile__unit">/30d</span>
          <span class="e360-cp-spark">
            <span class="e360-cp-bar" [style.height.%]="barPct(cp.daysPrev60)"
                  [title]="'61–90 days ago: ' + cp.daysPrev60 + ' days'"></span>
            <span class="e360-cp-bar" [style.height.%]="barPct(cp.daysPrev30)"
                  [title]="'31–60 days ago: ' + cp.daysPrev30 + ' days'"></span>
            <span class="e360-cp-bar e360-cp-bar--now" [style.height.%]="barPct(cp.daysLast30)"
                  [title]="'Last 30 days: ' + cp.daysLast30 + ' days'"></span>
          </span>
          <span class="e360-trenddelta"
                [class.e360-trenddelta--up]="cp.daysLast30 > cp.daysPrev30"
                [class.e360-trenddelta--down]="cp.daysLast30 < cp.daysPrev30"
                [title]="cp.daysLast30 > cp.daysPrev30
                  ? ('Up: used Copilot ' + (cp.daysLast30 - cp.daysPrev30) + ' more days than the previous 30-day window (' + cp.daysLast30 + ' vs ' + cp.daysPrev30 + ')')
                  : ('Down: used Copilot ' + (cp.daysPrev30 - cp.daysLast30) + ' fewer days than the previous 30-day window (' + cp.daysLast30 + ' vs ' + cp.daysPrev30 + ')')"
                *ngIf="cp.daysLast30 !== cp.daysPrev30">
            {{ cp.daysLast30 > cp.daysPrev30 ? '▲' : '▼' }}{{ cp.daysLast30 > cp.daysPrev30 ? (cp.daysLast30 - cp.daysPrev30) : (cp.daysPrev30 - cp.daysLast30) }}
          </span>
        </span>
        <span class="e360-kpi-tile__caption">
          {{ cp.activityStatus || '—' }} user
        </span>
      </div>
      <ng-template #copilotPending>
        <div class="e360-kpi-tile e360-kpi-tile--pending">
          <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">📅</span>Copilot · days active <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="tips.copilotDays">i</span></span>
          <span class="e360-pending-mark">⏳</span>
          <span class="e360-kpi-tile__caption">no Copilot activity recorded</span>
        </div>
      </ng-template>

      <!-- Remaining AI tiles — data sources still pending -->
      <div class="e360-kpi-tile e360-kpi-tile--pending" *ngFor="let t of [
            { label: 'LOC accepted', ic: '💻', caption: 'Copilot + Claude Code', tip: 'Lines of AI-suggested code accepted across Copilot and Claude Code.' }]">
        <span class="e360-kpi-tile__label"><span class="e360-ic e360-ic--sm e360-ic--violet">{{ t.ic }}</span>{{ t.label }} <span class="e360-ai-tag">AI</span><span class="e360-info" [attr.data-tip]="t.tip">i</span></span>
        <span class="e360-pending-mark">⏳</span>
        <span class="e360-kpi-tile__caption">{{ t.caption }} · coming soon</span>
      </div>
    </div>

    <!-- ═══════ Main grid ═══════ -->
    <div class="e360-grid">

      <!-- Left column -->
      <div class="e360-col">

        <!-- Productivity -->
        <section class="e360-card">
          <div class="e360-section-head">
            <span class="e360-section-title"><span class="e360-ic e360-ic--green">📊</span>Productivity · {{ currentYear }}<span class="e360-info" [attr.data-tip]="tips.productivity">i</span></span>
            <a class="e360-section-link" href="/productivity/dashboard">Open Productivity →</a>
          </div>
          <p class="e360-empty" *ngIf="productivityLoading">Loading…</p>
          <p class="e360-empty" *ngIf="!productivityLoading && metricCharts.length === 0">
            No productivity records yet — ask the scrum master to upload this month's file.
          </p>
          <div class="e360-metric-charts" *ngIf="metricCharts.length > 0">
            <div class="e360-metric-chart" *ngFor="let mc of metricCharts">
              <div class="e360-metric-chart__name">{{ mc.metricName }} <span class="e360-metric-chart__latest">{{ mc.latest }}</span></div>
              <svg class="e360-chart" viewBox="0 0 260 110" aria-hidden="true">
                <line class="e360-grid-line" x1="0" y1="20" x2="260" y2="20"/>
                <line class="e360-grid-line" x1="0" y1="55" x2="260" y2="55"/>
                <line class="e360-grid-line" x1="0" y1="90" x2="260" y2="90"/>
                <polyline class="e360-line--baseline" *ngIf="mc.baselinePoints" [attr.points]="mc.baselinePoints"/>
                <polyline class="e360-area" [attr.points]="mc.areaPoints"/>
                <polyline class="e360-line" [attr.points]="mc.linePoints"/>
                <circle class="e360-dot e360-dot--end" [attr.cx]="mc.endX" [attr.cy]="mc.endY" r="4"/>
                <text class="e360-axis-label" *ngFor="let al of mc.axisLabels" [attr.x]="al.x" y="103">{{ al.label }}</text>
              </svg>
            </div>
          </div>
        </section>

        <!-- T-Score trend — reconstructed from the audit log (Jul 2026 →);
             each point replays that day's skill map through the live scorer. -->
        <section class="e360-card">
          <div class="e360-section-head">
            <span class="e360-section-title"><span class="e360-ic e360-ic--mint">📈</span>T-Score · trend<span class="e360-info" [attr.data-tip]="tips.tsTrend">i</span></span>
            <a class="e360-section-link" (click)="openProfile('tmap')">Open T-Map →</a>
          </div>
          <ng-container *ngIf="tsTrend; else tsTrendPending">
            <svg class="e360-chart e360-chart--trend" viewBox="0 0 640 170" aria-hidden="true">
              <line class="e360-grid-line" *ngFor="let gy of tsTrend.gridYs"
                    x1="18" [attr.y1]="gy" x2="544" [attr.y2]="gy"/>
              <ng-container *ngIf="tsTrend.benchY != null">
                <line class="e360-line--baseline"
                      x1="18" [attr.y1]="tsTrend.benchY" x2="544" [attr.y2]="tsTrend.benchY"/>
                <text class="e360-trend-bench" x="552" [attr.y]="tsTrend.benchY + 3">
                  bench {{ data.tScore?.benchmark }}
                </text>
              </ng-container>
              <polyline class="e360-area" [attr.points]="tsTrend.areaPoints"/>
              <polyline class="e360-line" [attr.points]="tsTrend.linePoints"/>
              <ng-container *ngFor="let d of tsTrend.dots; let isLast = last">
                <circle class="e360-dot" [class.e360-dot--end]="isLast"
                        [attr.cx]="d.x" [attr.cy]="d.y" [attr.r]="isLast ? 4.5 : 3"/>
                <text class="e360-trend-val" [class.e360-trend-val--end]="isLast"
                      [attr.x]="d.x" [attr.y]="d.labelY" text-anchor="middle">{{ d.score }}</text>
              </ng-container>
              <text class="e360-axis-label" *ngFor="let al of tsTrend.axisLabels"
                    [attr.x]="al.x" y="164" [attr.text-anchor]="al.anchor">{{ al.label }}</text>
            </svg>
            <div class="e360-chart-note" *ngIf="data.tScore">
              Replayed from assessment history (since Jul 2026) · benchmark {{ data.tScore.benchmark }} dashed
            </div>
          </ng-container>
          <ng-template #tsTrendPending>
            <div class="e360-pending-block">
              <span class="e360-pending-mark">📈</span>
              <div>
                <div class="e360-pending-title">Not enough history yet</div>
                <div class="e360-pending-sub">
                  The trend draws from assessment saves (tracked since Jul 2026) — it appears
                  after the next skills update.
                  <ng-container *ngIf="data.tScore">Today: <strong>{{ data.tScore.current }}</strong>
                    vs benchmark {{ data.tScore.benchmark }}.</ng-container>
                </div>
              </div>
            </div>
          </ng-template>
        </section>

        <!-- Competencies — radar now vs earliest-known + movement -->
        <section class="e360-card">
          <div class="e360-section-head">
            <span class="e360-section-title">
              <span class="e360-ic e360-ic--blue">🕸️</span>
              Competencies
              <span class="e360-info" [attr.data-tip]="tips.competencies">i</span>
            </span>
            <a class="e360-section-link" (click)="openProfile('competencies')">Open Competencies →</a>
          </div>
          <div class="e360-radar-wrap" *ngIf="radar">
            <svg class="e360-radar" viewBox="0 0 320 224" aria-hidden="true">
              <polygon class="e360-radar__grid" *ngFor="let ring of radar.rings" [attr.points]="ring"/>
              <line class="e360-radar__axis" *ngFor="let a of radar.axes" x1="160" y1="112" [attr.x2]="a.x2" [attr.y2]="a.y2"/>
              <polygon class="e360-radar__value" [attr.points]="radar.nowPoints"/>
              <circle class="e360-radar__dot" *ngFor="let d of radar.nowDots" [attr.cx]="d.x" [attr.cy]="d.y" r="3"/>
              <text class="e360-radar__label" *ngFor="let l of radar.labels"
                    [attr.x]="l.x" [attr.y]="l.y" [attr.text-anchor]="l.anchor">{{ l.text }}</text>
            </svg>
            <div class="e360-radar-side">
              <ul class="e360-move-list">
                <li *ngFor="let c of data.competencies.current">
                  <span class="e360-move-name">{{ c.name }}</span>
                  <span class="e360-lvl-pill e360-lvl-pill--now">{{ c.level || '—' }}</span>
                </li>
              </ul>
            </div>
          </div>
          <p class="e360-empty" *ngIf="!radar">No competency ratings yet.</p>
        </section>
      </div>

      <!-- Right column -->
      <div class="e360-col">

        <!-- RTO -->
        <section class="e360-card">
          <div class="e360-section-head">
            <span class="e360-section-title"><span class="e360-ic e360-ic--amber">🏢</span>Return to Office · {{ currentYear }}<span class="e360-info" [attr.data-tip]="tips.rto">i</span></span>
            <a class="e360-section-link" (click)="openRto()">Open RTO →</a>
          </div>
          <p class="e360-empty" *ngIf="rtoLoading">Loading…</p>
          <ng-container *ngIf="!rtoLoading && rtoBars.length > 0">
            <svg class="e360-chart" viewBox="0 0 320 150" aria-hidden="true">
              <line class="e360-grid-line" x1="24" y1="30" x2="312" y2="30"/>
              <line class="e360-grid-line" x1="24" y1="75" x2="312" y2="75"/>
              <line class="e360-grid-line" x1="24" y1="120" x2="312" y2="120"/>
              <text class="e360-axis-label" x="2" y="33">12d</text>
              <text class="e360-axis-label" x="6" y="78">6d</text>
              <text class="e360-axis-label" x="6" y="123">0</text>
              <line class="e360-target-line" x1="24" y1="30" x2="312" y2="30"/>
              <rect *ngFor="let b of rtoBars" [attr.x]="b.x" [attr.y]="b.y" width="20" [attr.height]="b.height"
                    rx="3" [attr.fill]="b.color" [attr.opacity]="b.opacity">
                <title>{{ b.month }}: {{ b.days }} days</title>
              </rect>
              <text class="e360-axis-label" *ngFor="let b of rtoBars" [attr.x]="b.x" y="135">{{ b.month }}</text>
            </svg>
            <div class="e360-rto-legend">
              <span><span class="e360-swatch" style="background: var(--e360-met);"></span>≥ 12 days</span>
              <span><span class="e360-swatch" style="background: var(--e360-pending);"></span>8–11</span>
              <span><span class="e360-swatch" style="background: var(--e360-overdue);"></span>&lt; 8</span>
              <span *ngIf="rtoAvg != null">avg <strong>{{ rtoAvg }} d/mo</strong> · target 12</span>
            </div>
          </ng-container>
        </section>

        <!-- R&R — source pending -->
        <section class="e360-card">
          <div class="e360-section-head">
            <span class="e360-section-title"><span class="e360-ic e360-ic--gold">🏆</span>Recognition & Rewards · {{ currentYear }}<span class="e360-info" [attr.data-tip]="tips.rnr">i</span></span>
          </div>
          <ng-container *ngIf="data.rnr as rr; else rnrPending">
            <div class="e360-rnr-stats">
              <div class="e360-rnr-stat e360-rnr-stat--won">
                <span class="e360-rnr-stat__num">{{ rr.awardsWon }}</span>
                <span class="e360-rnr-stat__lbl">Awards won</span>
              </div>
            </div>
            <div class="e360-tags" *ngIf="rr.categories.length" style="margin-top: 12px;">
              <span class="e360-tag e360-tag--gold" *ngFor="let c of rr.categories">
                🏅 {{ c.name }}{{ c.count > 1 ? ' ×' + c.count : '' }}
              </span>
            </div>
            <p class="e360-cert-next" *ngIf="rr.lastAwardCategory">
              Last award: <strong>{{ rr.lastAwardCategory }}</strong>{{ rr.lastAwardDate ? ' · ' + (rr.lastAwardDate | date:'MMM y') : '' }}{{ rr.thisYearAwards > 0 ? ' · ' + rr.thisYearAwards + ' this year' : '' }}
            </p>
          </ng-container>
          <ng-template #rnrPending>
            <div class="e360-pending-block">
              <span class="e360-pending-mark">🏆</span>
              <div>
                <div class="e360-pending-title">Awards, nominations & shoutouts</div>
                <div class="e360-pending-sub">No R&R nominations recorded for this person.</div>
              </div>
            </div>
          </ng-template>
        </section>

        <!-- Certifications -->
        <section class="e360-card">
          <div class="e360-section-head">
            <span class="e360-section-title"><span class="e360-ic e360-ic--gold">🎓</span>Certifications<span class="e360-info" [attr.data-tip]="tips.certifications">i</span></span>
            <a class="e360-section-link" (click)="openProfile('goals')">Open Development →</a>
          </div>
          <div class="e360-succession">
            <div class="e360-succession__cell">
              <div class="e360-succession__cell-label">Active</div>
              <div class="e360-succession__cell-value">
                {{ data.certifications.activeCount }}{{ data.certifications.activeCount === 0 ? ' · none earned yet' : '' }}
              </div>
            </div>
            <div class="e360-succession__cell">
              <div class="e360-succession__cell-label">Expiring soon</div>
              <div class="e360-succession__cell-value"
                   [class.e360-succession__cell-value--warn]="data.certifications.expiringSoonCount > 0">
                {{ data.certifications.expiringSoonCount > 0
                   ? data.certifications.expiringSoonCount + ' within 60 days'
                   : 'None' }}
              </div>
            </div>
          </div>
          <p class="e360-cert-next" *ngIf="data.certifications.nextExpiryDays != null">
            Next expiry: <strong>{{ data.certifications.nextExpiryName || 'certification' }}</strong>
            in {{ data.certifications.nextExpiryDays }} days
          </p>
        </section>
      </div>
    </div>

    <!-- ═══════ Recommendations — full-width closing band ═══════ -->
    <section class="e360-card e360-card--wide" *ngIf="data.recommendations.length > 0">
      <div class="e360-section-head">
        <span class="e360-section-title"><span class="e360-ic e360-ic--amber">💡</span>Recommendations<span class="e360-info" [attr.data-tip]="tips.recommendations">i</span></span>
      </div>
      <ul class="e360-reco-list">
        <li *ngFor="let r of data.recommendations">
          <a class="e360-reco"
             [class.e360-reco--risk]="r.kind === 'tmap-gap'"
             [class.e360-reco--cert]="r.kind === 'cert-expiry'"
             (click)="navigateTo(r.route)">
            <span class="e360-reco__icon">{{
              r.kind === 'tmap-gap' ? '⚠️' : r.kind === 'cert-expiry' ? '📜'
              : r.kind === 'flat-competency' ? '🧭' : '🎯' }}</span>
            <span class="e360-reco__body">
              <span class="e360-reco__title">{{ r.title }}</span>
              <span class="e360-reco__why">{{ r.reason }}</span>
            </span>
            <span class="e360-reco__cta">{{ r.cta }} →</span>
          </a>
        </li>
      </ul>
    </section>
  </div>
</div>

<div class="e360-state" *ngIf="loading">Loading employee 360…</div>
<div class="e360-state" *ngIf="!loading && errored">
  Couldn't load this employee — they may be outside your reporting scope.
</div>
